/**
 * ProjectCard Component
 *
 * Displays project summary in a card layout
 * Shows: name, description, tags, status, dates, action buttons
 */

import React from 'react';
import { Project } from '../../types/project';
import { StatusBadge } from '../ui/StatusBadge';

interface ProjectCardProps {
  project: Project;
  onView: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
}

export const ProjectCard: React.FC<ProjectCardProps> = ({
  project,
  onView,
  onEdit,
  onDelete,
}) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  };

  const truncateDescription = (text: string | undefined, maxLength: number) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow p-6">
      {/* Header with name and status */}
      <div className="flex justify-between items-start mb-3">
        <h3 className="text-lg font-semibold text-gray-900 flex-1 mr-4">
          {project.name}
        </h3>
        <StatusBadge status={project.status} />
      </div>

      {/* Description */}
      {project.description && (
        <p className="text-sm text-gray-600 mb-4">
          {truncateDescription(project.description, 100)}
        </p>
      )}

      {/* Tags */}
      {project.tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {project.tags.map((tag, index) => (
            <span
              key={index}
              className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded border border-gray-200"
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Dates */}
      <div className="text-xs text-gray-500 mb-4 space-y-1">
        <div>Created: {formatDate(project.created_at)}</div>
        <div>Updated: {formatDate(project.updated_at)}</div>
      </div>

      {/* Action buttons */}
      <div className="flex gap-2">
        <button
          onClick={() => onView(project.id)}
          className="flex-1 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          aria-label={`View ${project.name}`}
        >
          View
        </button>
        <button
          onClick={() => onEdit(project.id)}
          className="px-4 py-2 bg-white text-gray-700 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
          aria-label={`Edit ${project.name}`}
        >
          Edit
        </button>
        <button
          onClick={() => onDelete(project.id)}
          className="px-4 py-2 bg-white text-red-600 text-sm font-medium rounded-md border border-red-300 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
          aria-label={`Delete ${project.name}`}
        >
          Delete
        </button>
      </div>
    </div>
  );
};
